import { useState, useEffect, useRef, useMemo } from 'react';
import { useWakeLock } from '@/hooks/useWakeLock';
import { useVoiceGuidance } from '@/hooks/useVoiceGuidance';
import { Slider } from '@/components/ui/slider';
import { StepperInput } from './StepperInput';
import { Play, Pause, Volume2, VolumeX, Home, X, RotateCcw, ImageIcon, Upload, Check } from 'lucide-react';
import { playTibetanBowl, initVoices } from '@/utils/audioUtils';
import { EXERCISE_BACKGROUNDS, EXERCISE_BG_FALLBACK_COLOR } from '@/utils/exerciseBackgrounds';
import ExerciseCopyright from './ExerciseCopyright';
import ExerciseInstructionsDrawer from './ExerciseInstructionsDrawer';
import ExerciseTopLogo from './ExerciseTopLogo';

// Predefined background options
const PRESET_BACKGROUNDS = [
  { id: 'video', label: 'Vidéo (défaut)', type: 'video', url: '/videos/coherence-bg.mp4' },
  { id: 'ocean', label: 'Océan', type: 'image', url: 'https://images.unsplash.com/photo-1474540412665-1cdae210ae6b?w=1920&q=80' },
  { id: 'lake', label: 'Lac', type: 'image', url: 'https://images.unsplash.com/photo-1621519997950-158b631341b6?w=1920&q=80' },
  { id: 'sunset', label: 'Coucher de soleil', type: 'image', url: 'https://images.unsplash.com/photo-1491929536571-bdbc57e72324?w=1920&q=80' },
  { id: 'forest', label: 'Forêt', type: 'image', url: 'https://images.unsplash.com/photo-1767352137704-e7521e8fb6e6?w=1920&q=80' },
];

const CoherenceModal = ({ 
  isOpen, 
  onClose, 
  pattern,
  onSessionComplete 
}) => {
  // Session states
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [phase, setPhase] = useState('ready');
  useWakeLock(isActive && !isPaused);
  useVoiceGuidance(phase, isActive && !isPaused);
  const [progress, setProgress] = useState(0);
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0); // Track total elapsed time
  
  // Coherence modal states
  const [sessionDuration, setSessionDuration] = useState(5); // minutes
  const [remainingTime, setRemainingTime] = useState(300); // seconds (5 min default)
  
  // Coherence breathing timing presets
  const timingPresets = [
    { id: '4-6', label: '4s / 6s', inhale: 4, holdIn: 0, exhale: 6, holdOut: 0 },
    { id: '5-5', label: '5s / 5s', inhale: 5, holdIn: 0, exhale: 5, holdOut: 0 },
    { id: '4-1-6-1', label: '4s-1s-6s-1s', inhale: 4, holdIn: 1, exhale: 6, holdOut: 1 }
  ];
  const [selectedPreset, setSelectedPreset] = useState(timingPresets[0]);
  
  // Background customization state
  const [showBackgroundPicker, setShowBackgroundPicker] = useState(false);
  const [selectedBackground, setSelectedBackground] = useState(() => {
    const saved = localStorage.getItem('coherence-background');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return PRESET_BACKGROUNDS[0];
      }
    }
    return PRESET_BACKGROUNDS[0];
  });
  const [customBackground, setCustomBackground] = useState(() => {
    return localStorage.getItem('coherence-custom-background') || null;
  });
  const fileInputRef = useRef(null);
  
  // Refs
  const soundEnabledRef = useRef(true);
  const videoRef = useRef(null);
  
  // Keep soundEnabledRef in sync
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);
  
  // Initialize voices on mount
  useEffect(() => {
    initVoices();
    const timeout = setTimeout(() => initVoices(), 100);
    return () => clearTimeout(timeout);
  }, []);

  // Handle background selection
  const handleSelectBackground = (bg) => {
    setSelectedBackground(bg);
    localStorage.setItem('coherence-background', JSON.stringify(bg));
    setShowBackgroundPicker(false);
  };

  // Handle custom image upload
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('Image trop volumineuse. Maximum 5 Mo.');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result;
        if (dataUrl) {
          setCustomBackground(dataUrl);
          localStorage.setItem('coherence-custom-background', dataUrl);
          const customBg = { id: 'custom', label: 'Ma photo', type: 'image', url: dataUrl };
          setSelectedBackground(customBg);
          localStorage.setItem('coherence-background', JSON.stringify(customBg));
          setShowBackgroundPicker(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Calculate bubble position based on phase and progress
  const bubblePosition = useMemo(() => {
    if (phase === 'inhale') {
      return 10 + (progress / 100) * 70;
    } else if (phase === 'exhale') {
      return 80 - (progress / 100) * 70;
    } else if (phase === 'hold-in') {
      return 80;
    } else if (phase === 'hold-out') {
      return 10;
    }
    return 10;
  }, [phase, progress]);
  
  // Breathing cycle logic
  useEffect(() => {
    if (!isOpen || !isActive || isPaused) return;

    const inhaleDuration = selectedPreset.inhale;
    const holdAfterInhale = selectedPreset.holdIn;
    const exhaleDuration = selectedPreset.exhale;
    const holdAfterExhale = selectedPreset.holdOut;

    const phases = [
      { name: 'inhale', duration: inhaleDuration },
      { name: 'hold-in', duration: holdAfterInhale },
      { name: 'exhale', duration: exhaleDuration },
      { name: 'hold-out', duration: holdAfterExhale },
    ].filter(p => p.duration > 0);

    let currentPhaseIndex = phases.findIndex(p => p.name === phase);
    if (currentPhaseIndex === -1) currentPhaseIndex = 0;

    const currentPhaseDuration = phases[currentPhaseIndex].duration * 1000;
    const updateInterval = 50;
    let elapsed = 0;

    const interval = setInterval(() => {
      elapsed += updateInterval;
      const newProgress = (elapsed / currentPhaseDuration) * 100;

      if (newProgress >= 100) {
        const nextPhaseIndex = (currentPhaseIndex + 1) % phases.length;
        const nextPhaseName = phases[nextPhaseIndex].name;
        setPhase(nextPhaseName);
        setProgress(0);
        
        if (soundEnabledRef.current) {
          if (nextPhaseName === 'inhale') {
            playTibetanBowl('low');
          } else if (nextPhaseName === 'exhale') {
            playTibetanBowl('high');
          }
        }

        if (nextPhaseIndex === 0) {
          setCyclesCompleted(prev => prev + 1);
        }
      } else {
        setProgress(newProgress);
      }
    }, updateInterval);

    return () => clearInterval(interval);
  }, [isOpen, isActive, isPaused, phase, selectedPreset]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || !isActive || isPaused) return;
    
    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1); // Track elapsed time
      setRemainingTime(prev => {
        if (prev <= 1) {
          // Session completed naturally - save it
          stopSession(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isOpen, isActive, isPaused]);

  // Play video when modal opens
  useEffect(() => {
    if (isOpen && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [isOpen]);

  const startSession = () => {
    setRemainingTime(sessionDuration * 60);
    setElapsedTime(0);
    setIsActive(true);
    setIsPaused(false);
    setPhase('inhale');
    setProgress(0);
    setCyclesCompleted(0);
  };

  const stopSession = (saveSession = false) => {
    // Save session if requested and conditions are met
    if (saveSession && onSessionComplete && elapsedTime > 10) {
      onSessionComplete({
        patternId: pattern?.id || 'coherence',
        patternName: pattern?.name || 'Cohérence Cardiaque',
        durationSeconds: elapsedTime,
        cyclesCompleted: cyclesCompleted
      });
    }
    
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setProgress(0);
    setCyclesCompleted(0);
    setElapsedTime(0);
  };

  // Reset session (go back to settings without saving)
  const resetSession = () => {
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setProgress(0);
    setCyclesCompleted(0);
    setElapsedTime(0);
    setRemainingTime(sessionDuration * 60);
  };

  const handleClose = () => {
    // Save session when closing if user practiced for at least 10 seconds
    stopSession(elapsedTime > 10);
    onClose();
  };

  if (!isOpen || !pattern) return null;

  const bgUrl = selectedBackground.id === 'custom' && customBackground 
    ? customBackground 
    : selectedBackground.url;

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden"
      style={{ 
        backgroundImage: selectedBackground.type === 'image' ? `url(${bgUrl})` : `url(${EXERCISE_BACKGROUNDS.coherence})`,
        backgroundColor: EXERCISE_BG_FALLBACK_COLOR,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <ExerciseCopyright />
      {/* Video Background - only show if video type selected */}
      {selectedBackground.type === 'video' && (
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          onLoadedData={() => {
            if (videoRef.current) {
              videoRef.current.play().catch(console.error);
            }
          }}
        >
          <source src={selectedBackground.url} type="video/mp4" />
        </video>
      )}

      {/* Image Background overlay for better text readability */}
      {selectedBackground.type === 'image' && (
        <div className="absolute inset-0 bg-black/20" />
      )}
      
      {/* Floating Navigation Buttons */}
      <div className="absolute top-4 left-4 z-20">
        <button
          onClick={handleClose}
          className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
          title="Retour aux exercices"
        >
          <Home size={20} className="text-white" />
        </button>
      </div>
      
      <div className="absolute top-4 right-4 z-20">
        <button
          onClick={handleClose}
          className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
          title="Fermer"
        >
          <X size={20} className="text-white" />
        </button>
      </div>

        <ExerciseTopLogo />

      {/* Main Content */}
      <div className="relative z-10 w-full h-full overflow-y-auto px-4 pt-20 sm:pt-24 pb-8 flex flex-col lg:items-center lg:justify-center lg:pb-8">
        <div className="flex flex-col items-center my-auto gap-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-10 lg:items-center w-full">
          
          {/* Left Panel - Settings */}
          <div className="w-full max-w-xs order-3 lg:order-none lg:col-start-1 lg:row-start-1 lg:justify-self-end lg:w-64">
            {/* Timer displayed as a stats card (moved from the top-center overlay
                to keep the header area clean, matching the ReduceFrequencyModal layout) */}
            <div className="bg-white/85 backdrop-blur-md rounded-xl px-3 py-3 text-center border border-slate-300 shadow-md mb-4">
              <p className="text-slate-500 text-xs mb-1">Temps restant</p>
              <p className="text-slate-800 font-bold text-2xl" style={{ fontFamily: 'system-ui' }}>
                {String(Math.floor(remainingTime / 60)).padStart(2, '0')}:{String(remainingTime % 60).padStart(2, '0')}
              </p>
            </div>
            {(!isActive || isPaused) && (
              <div className="bg-white/90 backdrop-blur rounded-2xl px-4 py-3 shadow-lg">
                <h3 className="font-semibold text-slate-700 text-sm mb-2">
                  {isPaused ? 'Pause - Réglages' : 'Réglages'}
                </h3>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-slate-600 font-medium text-xs">Durée</span>
                    <StepperInput
                      value={sessionDuration}
                      onChange={(v) => {
                        setSessionDuration(v);
                        if (isPaused) setRemainingTime(v * 60);
                      }}
                      min={5}
                      max={30}
                      step={1}
                      unit="min"
                      label="durée"
                      colorClass="cyan"
                      valueTestId="coherence-duration-value"
                      incTestId="coherence-duration-inc"
                      decTestId="coherence-duration-dec"
                    />
                  </div>
                  
                  <div className="flex flex-col gap-1.5">
                    <span className="text-slate-600 font-medium text-xs">Rythme</span>
                    <div className="flex flex-row gap-1.5 flex-wrap">
                      {timingPresets.map((preset) => (
                        <button
                          key={preset.id}
                          onClick={() => setSelectedPreset(preset)}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all flex-1 ${
                            selectedPreset.id === preset.id
                              ? 'bg-cyan-500 text-white shadow-md'
                              : 'bg-white/70 text-slate-600 hover:bg-white border border-slate-200'
                          }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Background customization button */}
                  <button
                    onClick={() => setShowBackgroundPicker(true)}
                    className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all border-t border-slate-200 pt-2 mt-1"
                  >
                    <ImageIcon size={14} />
                    Fond : {selectedBackground.label}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Center Column - Bubble + Phase */}
          <div className="flex flex-col items-center order-1 lg:order-none lg:col-start-2 lg:row-start-1">
            <div 
              className="relative rounded-3xl overflow-hidden shadow-xl"
              style={{
                width: '100px',
                height: '55vh',
                maxHeight: '500px',
                minHeight: '300px',
                background: 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(200,215,220,0.9))',
                border: '3px solid rgba(180,195,200,0.5)'
              }}
            >
              <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(to bottom, rgba(220,235,240,0.3) 0%, rgba(180,200,210,0.4) 100%)'
                }}
              />
              
              <div
                className="absolute left-1/2 transform -translate-x-1/2 transition-all"
                style={{
                  width: '65px',
                  height: '65px',
                  bottom: `${bubblePosition}%`,
                  transitionDuration: isActive && !isPaused ? '100ms' : '0ms',
                  transitionTimingFunction: 'linear'
                }}
              >
                <div
                  className="relative w-full h-full rounded-full"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,1) 0%, rgba(100,180,220,1) 50%, rgba(50,140,200,1) 100%)',
                    boxShadow: 'inset -6px -6px 15px rgba(0,0,0,0.2), inset 4px 4px 10px rgba(255,255,255,1), 0 6px 20px rgba(0,0,0,0.4), 0 0 20px rgba(100,180,255,0.6)'
                  }}
                >
                  <div 
                    className="absolute rounded-full"
                    style={{
                      width: '22px',
                      height: '14px',
                      top: '10px',
                      left: '12px',
                      background: 'linear-gradient(to bottom, rgba(255,255,255,1), rgba(255,255,255,0.7))',
                      transform: 'rotate(-20deg)'
                    }}
                  />
                </div>
              </div>

              {[
                { size: 12, left: 25, bottom: 15 },
                { size: 8, left: 55, bottom: 22 },
                { size: 15, left: 35, bottom: 32 },
                { size: 10, left: 60, bottom: 42 },
                { size: 9, left: 20, bottom: 50 },
                { size: 14, left: 50, bottom: 58 },
                { size: 11, left: 30, bottom: 68 },
                { size: 8, left: 65, bottom: 75 },
              ].map((bubble, i) => (
                <div
                  key={i}
                  className="absolute rounded-full opacity-60"
                  style={{
                    width: `${bubble.size}px`,
                    height: `${bubble.size}px`,
                    left: `${bubble.left}%`,
                    bottom: `${bubble.bottom}%`,
                    background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9), rgba(200,220,230,0.4))',
                    boxShadow: 'inset -2px -2px 5px rgba(0,0,0,0.05), inset 1px 1px 3px rgba(255,255,255,0.7)'
                  }}
                />
              ))}
            </div>

            {isActive && (
              <div className="mt-3 sm:mt-4 bg-white/80 backdrop-blur rounded-xl px-4 sm:px-6 py-1.5 sm:py-2 shadow-lg">
                <span className="text-base sm:text-lg font-medium text-slate-600">
                  {phase === 'inhale' ? 'Inspirez' : phase === 'exhale' ? 'Expirez' : phase === 'hold-in' ? 'Retenez' : phase === 'hold-out' ? 'Retenez' : 'Prêt'}
                </span>
              </div>
            )}
          </div>

          {/* Right Panel - Controls */}
          <div className="w-full max-w-xs flex-shrink-0 flex justify-center lg:justify-start order-2 lg:order-none lg:col-start-3 lg:row-start-1 lg:justify-self-start lg:w-auto">
            <div className="flex flex-row lg:flex-col gap-3 sm:gap-4 exercise-controls-mobile-fixed lg:items-center">
              <button
                onClick={() => {
                  if (!isActive) {
                    setRemainingTime(sessionDuration * 60);
                    startSession();
                  } else {
                    setIsPaused(!isPaused);
                  }
                }}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(180,195,200,0.5)' }}
              >
                {!isActive || isPaused ? (
                  <Play size={28} className="text-slate-600 ml-1 sm:w-9 sm:h-9" />
                ) : (
                  <Pause size={28} className="text-slate-600 sm:w-9 sm:h-9" />
                )}
              </button>

              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(180,195,200,0.5)' }}
              >
                {soundEnabled ? (
                  <Volume2 size={22} className="text-slate-600 sm:w-7 sm:h-7" />
                ) : (
                  <VolumeX size={22} className="text-slate-600 sm:w-7 sm:h-7" />
                )}
              </button>
              
              {/* Reset button - only show when active */}
              {isActive && phase !== 'ready' && (
                <button
                  onClick={resetSession}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                  style={{ border: '3px solid rgba(239, 68, 68, 0.5)' }}
                  title="Réinitialiser"
                >
                  <RotateCcw size={22} className="text-red-500 sm:w-7 sm:h-7" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Instructions drawer (desktop only) */}
      <ExerciseInstructionsDrawer testIdPrefix="coherence">
        <div className="bg-white rounded-xl p-4 shadow border border-slate-200">
          <h3 className="font-semibold text-slate-700 mb-3">Instructions</h3>
          <ul className="text-sm text-slate-600 space-y-2">
            <li className="flex items-start gap-2"><span className="text-cyan-500 font-bold">1.</span>Suivez la bulle avec votre respiration</li>
            <li className="flex items-start gap-2"><span className="text-cyan-500 font-bold">2.</span>Inspirez quand elle monte</li>
            <li className="flex items-start gap-2"><span className="text-blue-500 font-bold">3.</span>Expirez quand elle descend</li>
            <li className="flex items-start gap-2"><span className="text-slate-500 font-bold">4.</span>Respirez par le nez, posément</li>
          </ul>
          <div className="mt-4 p-3 bg-cyan-50 rounded-lg">
            <p className="text-xs text-cyan-700">
              <strong>Cohérence cardiaque :</strong> 5 minutes, 3 fois par jour. Équilibre le système nerveux autonome.
            </p>
          </div>
        </div>
      </ExerciseInstructionsDrawer>

      {/* Background Picker Modal */}
      {showBackgroundPicker && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800">Personnaliser le fond</h3>
              <button 
                onClick={() => setShowBackgroundPicker(false)}
                className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              >
                <X size={20} className="text-slate-500" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {/* Preset backgrounds */}
              <p className="text-sm text-slate-500 mb-3">Fonds prédéfinis</p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {PRESET_BACKGROUNDS.map((bg) => (
                  <button
                    key={bg.id}
                    onClick={() => handleSelectBackground(bg)}
                    className={`relative rounded-xl overflow-hidden aspect-video border-2 transition-all ${
                      selectedBackground.id === bg.id 
                        ? 'border-cyan-500 ring-2 ring-cyan-200' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {bg.type === 'video' ? (
                      <div className="w-full h-full bg-gradient-to-br from-teal-400 to-cyan-600 flex items-center justify-center">
                        <Play size={24} className="text-white" />
                      </div>
                    ) : (
                      <img 
                        src={bg.url} 
                        alt={bg.label}
                        className="w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <span className="text-white text-xs font-medium">{bg.label}</span>
                    </div>
                    {selectedBackground.id === bg.id && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center">
                        <Check size={14} className="text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Custom upload */}
              <p className="text-sm text-slate-500 mb-3">Ma photo</p>
              <div className="space-y-3">
                {customBackground && (
                  <button
                    onClick={() => handleSelectBackground({ id: 'custom', label: 'Ma photo', type: 'image', url: customBackground })}
                    className={`relative w-full rounded-xl overflow-hidden aspect-video border-2 transition-all ${
                      selectedBackground.id === 'custom' 
                        ? 'border-cyan-500 ring-2 ring-cyan-200' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <img 
                      src={customBackground} 
                      alt="Ma photo"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <span className="text-white text-xs font-medium">Ma photo</span>
                    </div>
                    {selectedBackground.id === 'custom' && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center">
                        <Check size={14} className="text-white" />
                      </div>
                    )}
                  </button>
                )}
                
                {/* iOS-compatible file upload - use label wrapping input for better touch support */}
                <label
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 hover:border-cyan-400 hover:text-cyan-600 transition-all cursor-pointer active:bg-slate-50"
                >
                  <Upload size={20} />
                  <span className="font-medium">
                    {customBackground ? 'Changer ma photo' : 'Choisir depuis ma photothèque'}
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="sr-only"
                  />
                </label>
                <p className="text-xs text-slate-400 text-center">
                  Format JPG, PNG. Maximum 5 Mo.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoherenceModal;
